import PropTypes from 'prop-types';
import { Typography } from '@nextiva/next-ui-components';

import {
  checkNumbersOnly,
  checkRoutingNumber,
  checkTextOnly,
  checkValidAddress,
  checkValidAddressNoMandatory,
  checkValidZipCode,
} from 'utils';
import { PaymentMethodPicker } from '../PaymentMethodPicker';
import { PaymentDrawerFooter } from '../PaymentDrawerFooter';
import { BillingInfo } from '../BillingInfo';

import './PaymentDrawerContent.scss';

const blockName = 'payment-drawer-content';

/**
 * Payment Drawer Content Component.
 * Includes Payment Method Picker, Billing Info and Footer.
 *
 * @param billingInfoConfig - Configuration for Billing Info component.
 * @param isSummaryPage - Flag indicating if it's a summary page.
 * @param paymentFooterConfig - Configuration for Payment Drawer Footer component.
 * @param paymentPickerConfig - Configuration for Payment Method Picker component.
 * @param showSpinner - Flag to show or hide spinner.
 */
const PaymentDrawerContent = ({
  billingInfoConfig,
  isSummaryPage,
  paymentFooterConfig,
  paymentPickerConfig,
  showSpinner,
}) => (
  <div
    className={blockName}
    data-testid="BillingLicensing-PaymentDrawerContent"
    style={{ display: showSpinner ? 'none' : 'block' }}
  >
    <Typography className={`${blockName}__title`}>Payment Details</Typography>
    <PaymentMethodPicker
      accountNumber={paymentPickerConfig.accountNumber}
      accountType={paymentPickerConfig.accountType}
      bankName={paymentPickerConfig.bankName}
      configDrawer={paymentPickerConfig.configDrawer}
      handleAccountNumberChange={paymentPickerConfig.handleAccountNumberChange}
      handleAccountTypeChange={paymentPickerConfig.handleAccountTypeChange}
      handleBankNameChange={paymentPickerConfig.handleBankNameChange}
      handlePaymentOptionChange={paymentPickerConfig.handlePaymentOptionChange}
      handleRoutingNumberChange={paymentPickerConfig.handleRoutingNumberChange}
      onVantivActionsLoad={paymentPickerConfig.onVantivActionsLoad}
      paymentOption={paymentPickerConfig.paymentOption}
      routingNumber={paymentPickerConfig.routingNumber}
      validateNumbersOnly={checkNumbersOnly}
      validateRoutingNumber={checkRoutingNumber}
      validateTextOnly={checkTextOnly}
    />
    <div className={`${blockName}__info-block`}>
      <span className={`${blockName}__info-icon`}>i</span>
      <Typography className={`${blockName}__info-text`} variant="caption1">
        We will never store your actual payment data (e.g. credit card numbers)
        but only store a "token" that allows us to bill your payment method
        without storing any of your personal information.
      </Typography>
    </div>
    <BillingInfo
      billingAddress1={billingInfoConfig.billingAddress1}
      billingAddress2={billingInfoConfig.billingAddress2}
      checkTextOnly={checkTextOnly}
      checkValidAddress={checkValidAddress}
      checkValidAddressNoMandatory={checkValidAddressNoMandatory}
      checkValidZipCode={checkValidZipCode}
      city={billingInfoConfig.city}
      firstName={billingInfoConfig.firstName}
      isSummaryPage={isSummaryPage}
      lastName={billingInfoConfig.lastName}
      setBillingAddress1={billingInfoConfig.handleBillingAddress1Change}
      setBillingAddress2={billingInfoConfig.handleBillingAddress2Change}
      setCity={billingInfoConfig.handleCityChange}
      setFirstName={billingInfoConfig.handleFirstNameChange}
      setLastName={billingInfoConfig.handleLastNameChange}
      setShouldBeDefault={billingInfoConfig.handleDefaultCardChange}
      setUSState={billingInfoConfig.handleUSStateChange}
      setZipCode={billingInfoConfig.handleZipCodeChange}
      shouldBeDefault={billingInfoConfig.shouldBeDefault}
      usState={billingInfoConfig.usState}
      zipCode={billingInfoConfig.zipCode}
    />
    <PaymentDrawerFooter
      amount={paymentFooterConfig.amount}
      callVantivRegistration={paymentFooterConfig.callVantivRegistration}
      close={paymentFooterConfig.close}
      isSummaryPage={isSummaryPage}
      submitButtonDisabled={paymentFooterConfig.submitButtonDisabled}
    />
  </div>
);

PaymentDrawerContent.propTypes = {
  billingInfoConfig: PropTypes.shape({
    billingAddress1: PropTypes.string.isRequired,
    billingAddress2: PropTypes.string,
    city: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    handleBillingAddress1Change: PropTypes.func.isRequired,
    handleBillingAddress2Change: PropTypes.func.isRequired,
    handleCityChange: PropTypes.func.isRequired,
    handleFirstNameChange: PropTypes.func.isRequired,
    handleLastNameChange: PropTypes.func.isRequired,
    handleDefaultCardChange: PropTypes.func.isRequired,
    handleUSStateChange: PropTypes.func.isRequired,
    handleZipCodeChange: PropTypes.func.isRequired,
    lastName: PropTypes.string.isRequired,
    shouldBeDefault: PropTypes.bool.isRequired,
    usState: PropTypes.string.isRequired,
    zipCode: PropTypes.string.isRequired,
  }).isRequired,
  isSummaryPage: PropTypes.bool.isRequired,
  paymentFooterConfig: PropTypes.shape({
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    callVantivRegistration: PropTypes.func.isRequired,
    close: PropTypes.func.isRequired,
    submitButtonDisabled: PropTypes.bool.isRequired,
  }).isRequired,
  paymentPickerConfig: PropTypes.shape({
    accountNumber: PropTypes.string.isRequired,
    accountType: PropTypes.string.isRequired,
    bankName: PropTypes.string.isRequired,
    configDrawer: PropTypes.object.isRequired,
    handleAccountNumberChange: PropTypes.func.isRequired,
    handleAccountTypeChange: PropTypes.func.isRequired,
    handleBankNameChange: PropTypes.func.isRequired,
    handlePaymentOptionChange: PropTypes.func.isRequired,
    handleRoutingNumberChange: PropTypes.func.isRequired,
    onVantivActionsLoad: PropTypes.func.isRequired,
    paymentOption: PropTypes.string.isRequired,
    routingNumber: PropTypes.string.isRequired,
  }).isRequired,
  showSpinner: PropTypes.bool.isRequired,
};

export default PaymentDrawerContent;
